import { useState, useEffect } from "react";
import { observer, Observer } from "mobx-react";
import { action, makeAutoObservable } from "mobx";
import { NumericFormat } from "react-number-format";
import TaskActuals from "./TaskActuals";
import TaskAnalysis from "./TaskAnalysis";
import TaskBaselines from "./TaskBaselines";
import TaskPeriodWeights from "./TaskPeriodWeights";
import TaskDistributionPicker from "./TaskDistributionPicker";
import ForecastMethodPicker from "./ForecastMethodPicker";
import { getUpdatedCost } from "../util/TaskUtil";
import {
  Paper, TextField, ButtonGroup, Button, Box,
  Tooltip, IconButton, Typography,
  Icon
} from "@mui/material";
import { debounce } from '@mui/material/utils';
import { UpdateTask } from "../operations/TaskOperations"
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CheckIcon from "@mui/icons-material/Check";
import validator from "validator";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { useViewState } from "../model/ViewState";
import { useProjectStore } from "../model/ProjectStore";
import Task from "../model/Task";
import { useSnackbar } from "notistack";
import { runInAction } from "mobx";
import Grid from '@mui/material/Grid';
import { DateTime } from "luxon";
import { GetFAC } from "../operations/TaskOperations";

class TaskPanelFormData {
  plannedStartDate = DateTime.min;
  plannedEndDate = DateTime.min;
  actualStartDate = DateTime.min;
  actualEndDate = DateTime.min;
  taskCode = "";
  taskName = "";
  plannedCost = 0.0;
  editEnabled = false;

  constructor(task) {
    this.plannedStartDate = DateTime.fromISO(task.plannedStartDate);
    this.plannedEndDate = DateTime.fromISO(task.plannedEndDate);
    this.actualStartDate = DateTime.fromISO(task.actualStartDate);
    this.actualEndDate = DateTime.fromISO(task.actualEndDate);
    this.taskCode = task.code;
    this.taskName = task.name;
    this.plannedCost = task.plannedCost;
    this.editEnabled = task.isNew;
    makeAutoObservable(this);
  }
}

const View = ({ task, tab, classes }) => {
  if (tab === "analysis") {
    return <TaskAnalysis task={task} classes={classes} />;
  } else if (tab === "baseline") {
    return <TaskBaselines task={task} classes={classes} readOnly={false} />;
  } else if (tab === "periodWeights") {
    return <TaskPeriodWeights task={task} classes={classes} />;
  } else {
    return <TaskActuals task={task} readOnly={false} />;
  }
};

const updateParentPlannedCost = (task) => {
  let parentTaskPackage = null;
  if (task.code !== "") {
    parentTaskPackage = task.getParentTaskPackage();
  }
  if (parentTaskPackage !== null && parentTaskPackage !== undefined) {
    parentTaskPackage.plannedCost = getUpdatedCost(parentTaskPackage);
    updateParentPlannedCost(parentTaskPackage);
  }
};

const TaskPanel = () => {
  const { enqueueSnackbar } = useSnackbar();
  const view = useViewState();

  const project = useProjectStore();
  const [FACValues, setFACValues] = useState({ None: 0 });
  
  let selectedTask = project.rootTaskPackage
    .depthFirstTraversal()
    .find((t) => t.id === view.selectedTaskId) ?? project.rootTaskPackage;

  const formData = new TaskPanelFormData(selectedTask);

  const undoFormData = action(() => {
    formData.plannedStartDate = DateTime.fromISO(selectedTask.plannedStartDate);
    formData.plannedEndDate = DateTime.fromISO(selectedTask.plannedEndDate);
    formData.actualStartDate = DateTime.fromISO(selectedTask.actualStartDate);
    formData.actualEndDate = DateTime.fromISO(selectedTask.actualEndDate);
    formData.taskCode = selectedTask.code;
    formData.taskName = selectedTask.name;
    formData.plannedCost = selectedTask.plannedCost;
    formData.editEnabled = selectedTask.isNew;
  })

  const saveFormData = action (() => {
    selectedTask.plannedStartDate = formData.plannedStartDate.toISODate();
    selectedTask.plannedEndDate = formData.plannedEndDate.toISODate();
    selectedTask.actualStartDate = formData.actualStartDate.toISODate();
    selectedTask.actualEndDate = formData.actualEndDate.toISODate();
    selectedTask.taskCode = formData.code;
    selectedTask.taskName = formData.name;
    selectedTask.plannedCost = formData.plannedCost;
    UpdateTask(project, selectedTask, action((result) => {
        let newTask = Task.fromJS(project, result, selectedTask.parent);
        const tasks = selectedTask.getParentTaskPackage().tasks;
        let idx = tasks.findIndex(t => t.id == selectedTask.id);
        tasks[idx] = newTask;
        selectedTask = newTask;
      })).catch((error) => {
        enqueueSnackbar(error.message, { variant: "error" });
      })
  })

  const changePlannedStartDate = () => {
    const initialEndDate = new Date(selectedTask.plannedEndDate);
    const dateDifferential = new Date(selectedTask.plannedStartDate) - new Date(selectedTask.plannedStartDate);

    runInAction(() => {
      
    });
  }

  const debouncePlannedStartDate = () => debounce(changePlannedStartDate, 1000);

  const handleChangePlannedStartDate = action((newValue) => {
    formData.plannedStartDate = newValue;
    //debouncePlannedStartDate();
  })

  useEffect(() => {
    GetFAC(selectedTask.id,
      (result => {
        setFACValues(result);
      }))
  }, [selectedTask])

  const observingTextField = (props) => (<Observer>{ () => <TextField {...props}/>}</Observer>)

  const changePlannedEndDate = () => {
    runInAction(() => {
      const newDate = DateTime.fromISO(formData.plannedEndDate);
      if (newDate.isValid) {
        selectedTask.plannedEndDate = formData.plannedEndDate;
        UpdateTask(project, selectedTask, (result) => {
          let newTask = Task.fromJS(project, result, selectedTask.parent);
          const tasks = selectedTask.getParentTaskPackage().tasks;
          let idx = tasks.findIndex(t => t.id == selectedTask.id);
          tasks[idx] = newTask;
          selectedTask = newTask;
        }).catch((error) => {
          enqueueSnackbar(error.message, { variant: "error" });
        })
      }
    });
  }

  const debouncedChangePlannedEndDate = () => debounce(changePlannedEndDate, 1000);

  const handleChangePlannedEndDate = action((newValue) => {
    formData.plannedEndDate = newValue;
    //debouncedChangePlannedEndDate()
  });

    const handleChangePlannedCost = action((newValue) => {
    formData.plannedCost = newValue.floatValue;
    //debouncedChangePlannedEndDate()
  });

  

  const handleChangeTaskName = (event) => {
    this.props.selectedTask.name = event.target.value;
  };

  const handleChangeTaskCode = (event) => {
    this.props.selectedTask.code = event.target.value;
  };

  const handleAnalysis = () => {
    this.setState({ selectedTab: "analysis" });
  };

  const handleActual = () => {
    this.setState({ selectedTab: "actual" });
  };

  const handleBaseline = () => {
    this.setState({ selectedTab: "baseline" });
  };

  const handlePeriodWeights = () => {

  };

  return (
    <Paper
      style={{
        minWidth: "1280px",
        minHeight: "400px",
        padding: "8px"
      }}
    >
      <Observer>{() => 
      <Grid container direction="row" width={650} spacing={2}>
        <Grid >
        <Typography color="textPrimary" variant="h6">
          Details
        </Typography>
        </Grid>
        <Grid offset="auto">
        {!formData.editEnabled && (
          <IconButton style={{ marginLeft: 'auto' }} onClick={action(() => formData.editEnabled = true)} >
            <EditIcon />
          </IconButton>)
        }
        {formData.editEnabled && (
          <>
          <IconButton style={{ marginLeft: 'auto' }} onClick={undoFormData} >
            <CancelIcon />
          </IconButton>
          <IconButton style={{ marginLeft: 'auto' }} onClick={saveFormData} >
            <CheckIcon />
          </IconButton>
          </>)
        }
        </Grid>
      </Grid>
      }</Observer>
      <TextField
        key={view.selectedTaskId + "_code"}
        id="task-code"
        label="Code"
        margin="normal"
        value={selectedTask.code}
        onChange={(e) => (runInAction(() => { selectedTask.code = e.target.value }))}
        variant="standard"
        slotProps={{
          input: {
            readOnly: false
          }
        }}
      />

      <TextField
        key={view.selectedTaskId + "_name"}
        id="task-name"
        label="Task Name"
        margin="normal"
        value={selectedTask.name}
        onChange={(e) => (runInAction(() => { selectedTask.name = e.target.value }))}
        variant="standard"
        slotProps={{
          input: {
            readOnly: false
          }
        }}
      />
      <Grid container>
        <ForecastMethodPicker task={selectedTask} />
        <NumericFormat
          key={view.selectedTaskId + "_cost"}
          id="forecast-at-completion"
          label="Forecast At Completion"
          value={FACValues[selectedTask.getRealForecastMethod()]}
          thousandSeparator
          decimalScale={2}
          prefix="$"
          customInput={TextField}
          variant="standard"
          slotProps={{
            input: {
              readOnly: true
            }
          }}
        />
      </Grid>
      <Typography color="textPrimary" variant="h6">
        Plan
      </Typography>
      <Grid container spacing={1.5} mt={1.5}>
        <Grid sx={{ ml: 1, maxWidth: "200px" }}>
          <Observer>{() => 
          <NumericFormat
            key={view.selectedTaskId + "_cost"}
            id="planned-cost"
            label="Planned Cost"
            value={formData.plannedCost}
            onValueChange={handleChangePlannedCost}
            thousandSeparator
            decimalScale={2}
            prefix="$"
            customInput={observingTextField}
            variant="standard"
            readOnly = {false}
            disabled = {!formData.editEnabled}
            slotProps={{
              input: {
                readOnly: false
              }}}
          />}</Observer>
        </Grid>
        <Grid sx={{ ml: 1, maxWidth: "200px"}}>
          <TextField
            type="number"
            key={view.selectedTaskid + "_duration"}
            id="planned-duration"
            label="Planned Duration"
            value={selectedTask.plannedDuration}
            variant="standard"
            slotProps={{
              input: {
                readOnly: true
              }
            }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1.5} mt={1.5}>
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <Grid sx={{ ml: 1, maxWidth: "200px" }}>
            <Observer>{() => 
              <DatePicker
                format="yyyy-MM-dd"
                id="planned-start-date-picker"
                label="Planned Start Date"
                value={formData.plannedStartDate}
                onChange={handleChangePlannedStartDate}
                readOnly = {!formData.editEnabled}
                slotProps={{
                  textField: { variant: "standard" },
                  readOnly: true
                }}
              />}
            </Observer>
            
          </Grid>
          <Grid  sx={{ ml: 1, maxWidth: "200px" }}>
            <Observer>{() => 
              <DatePicker
                format="yyyy-MM-dd"
                id="planned-end-date-picker"
                label="Planned End Date"
                value={formData.plannedEndDate}
                onChange={handleChangePlannedEndDate}
                minDate={DateTime.fromISO('2020-01-01')}
                readOnly = {!formData.editEnabled}
                slotProps={{
                  textField: { variant: "standard" },
                  readOnly: true
                }}
              />}
            </Observer>
          </Grid>
          <Grid>
            <Observer>
              {() => 
              <DatePicker
                format="yyyy-MM-dd"
                id="actual-start-date-picker"
                label="Actual Start Date"
                value={DateTime.fromISO(selectedTask.actualStartDate)}
                onChange={(e) => (runInAction(() => { selectedTask.actualStartDate = e }))}
                slotProps={{
                  textField: { variant: "standard" },
                  readOnly: false
                }}
              />}
            </Observer>
          </Grid>
        </LocalizationProvider>
      </Grid>

      <Grid container mt={2} spacing={1.5} >
        <TextField
          key={view.selectedTaskId + "_plannedStartPeriod"}
          id="planned-start-period"
          label="Start Period"
          value={selectedTask.plannedStartPeriod}
          variant="standard"
          slotProps={{
            input: {
              readOnly: true
            }
          }}
        />
        <TextField
          key={view.selectedTaskId + "_plannedEndPeriod"}
          id="planned-end-period"
          label="End Period"
          value={selectedTask.plannedEndPeriod}
          
        onChange={(e) => (runInAction(() => { selectedTask.plannedEndPeriod = e.target.value }))}
          variant="standard"
        />
        <TextField
          key={view.selectedTaskId + "_forecastedEndPeriod"}
          id="forecasted-end-period"
          label="Forecasted End Period"
          value={selectedTask.forecastedEndPeriod}
          variant="standard"
          slotProps={{
            input: {
              readOnly: true
            }
          }}
        />
      </Grid>

      <Typography color="textPrimary" variant="h6">
        Distribution
      </Typography>
      <TaskDistributionPicker task={selectedTask} />

      <hr />
      <ButtonGroup variant="text">
        <Button color="primary" onClick={() => view.setTaskViewSelectedTab("actual")}>Actuals</Button>
        <Button color="primary" onClick={() => view.setTaskViewSelectedTab("analysis")}>Analysis</Button>
        <Button color="primary" onClick={() => view.setTaskViewSelectedTab("baseline")}>Baselines</Button>
        <Button onClick={() => {alert('This feature is still a work in progress.');}}>Period Weights</Button>
      </ButtonGroup>

      <Box sx={{ width: "600px" }}>
        <View
          task={selectedTask}
          tab={view.taskViewSelectedTab}
        />
      </Box>
    </Paper>
  )
}

// class TaskPanel extends React.Component {
//   constructor(props) {
//     super(props);
//     this.state = {
//       selectedTab: "actual",
//     };
//   }

//   oldDurationValue = this.props.selectedTask.plannedDuration;

//   handleChangePlannedStartDate = (date) => {
//     if (validator.isDate(date, "MM/DD/YYYY")) {
//       let initalEndDate = new Date(this.props.selectedTask.plannedEndDate);
//       let dateDifferential =
//         new Date(date) - new Date(this.props.selectedTask.plannedStartDate);

//       this.props.selectedTask.plannedStartDate = date;
//       this.props.selectedTask.plannedEndDate = new Date(
//         initalEndDate.getTime() + dateDifferential
//       ).toISOString();
//       this.props.selectedTask.updateTask().catch((error) => {
//         this.props.enqueueSnackbar(error.message, { variant: "error" })
//       });
//     }
//   };

//   handleChangePlannedEndDate = (date) => {
//     if (validator.isDate(date, "MM/DD/YYYY")) {
//       this.props.selectedTask.plannedEndDate = date;
//       this.props.selectedTask.updateTask().catch((error) => {
//         this.props.enqueueSnackbar(error.message, { variant: "error" })
//       });
//     }
//   };

//   handleChangePlannedCost = (event) => {
//     this.props.selectedTask.plannedCost = event.floatValue;
//   };

//   handleChangeTaskName = (event) => {
//     this.props.selectedTask.name = event.target.value;
//   };

//   handleChangeTaskCode = (event) => {
//     this.props.selectedTask.code = event.target.value;
//   };

//   handleAnalysis = () => {
//     this.setState({ selectedTab: "analysis" });
//   };

//   handleActual = () => {
//     this.setState({ selectedTab: "actual" });
//   };

//   handleBaseline = () => {
//     this.setState({ selectedTab: "baseline" });
//   };

//   render() {
//     return (
//       <Paper style={{ minWidth: "800px", minHeight: "400px", padding: "8px" }}>
//         <Typography color="textPrimary" variant="h6">
//           Details
//         </Typography>
//         <TextField
//           key={this.props.view.selectedTaskId + "_code"}
//           id="taskCode"
//           label="Code"
//           className={this.props.classes.textField}
//           margin="normal"
//           value={this.props.selectedTask.code}
//           InputLabelProps={{
//             shrink: true,
//           }}
//           onChange={this.handleChangeTaskCode}
//         />
//         <TextField
//           key={this.props.view.selectedTaskId + "_name"}
//           id="taskName"
//           label="Task Name"
//           className={this.props.classes.textField}
//           margin="normal"
//           value={this.props.selectedTask.name}
//           InputLabelProps={{
//             shrink: true,
//           }}
//           onChange={this.handleChangeTaskName}
//         />
//         <ForecastMethodPicker task={this.props.selectedTask} />
//         <Typography color="textPrimary" variant="h6">
//           Plan
//         </Typography>
//         <div>
//           <NumericFormat
//             key={this.props.view.selectedTaskId + "_cost"}
//             id="plannedCost"
//             label="Planned Cost"
//             className={this.props.classes.textField}
//             InputLabelProps={{ shrink: true }}
//             value={this.props.selectedTask.plannedCost}
//             onValueChange={this.handleChangePlannedCost}
//             onBlur={() => updateParentPlannedCost(this.props.selectedTask)}
//             thousandSeparator
//             decimalScale={2}
//             prefix="$"
//             customInput={TextField}
//           />
//           <TextField
//             readOnly={true}
//             key={this.props.view.selectedTaskId + "_duration"}
//             id="plannedDuration"
//             label="Planned Duration"
//             className={this.props.classes.textField}
//             InputLabelProps={{ shrink: true }}
//             InputProps={{ inputProps: { type: "number" } }}
//             value={this.props.selectedTask.plannedDuration}
//           />
//         </div>
//         <div>
//           <LocalizationProvider dateAdapter={AdapterLuxon}>
//             <DatePicker
//               disableToolbar
//               variant="inline"
//               format="MM/dd/yyyy"
//               margin="normal"
//               id="planned-start-date-picker"
//               label="Planned Start Date"
//               value={this.props.selectedTask.plannedStartDate}
//               onChange={this.handleChangePlannedStartDate}
//               KeyboardButtonProps={{
//                 "aria-label": "change date",
//               }}
//             />
//             <DatePicker
//               disableToolbar
//               variant="inline"
//               format="MM/dd/yyyy"
//               margin="normal"
//               id="planned-end-date-picker"
//               label="Planned End Date"
//               value={this.props.selectedTask.plannedEndDate}
//               onChange={(e) => this.handleChangePlannedEndDate(e, true)}
//               KeyboardButtonProps={{
//                 "aria-label": "change date",
//               }}
//             />
//           </LocalizationProvider>
//         </div>
//         <div>
//           <TextField
//             readOnly={true}
//             key={this.props.view.selectedTaskId + "plannedStartPeriod"}
//             id="plannedStartPeriod"
//             label="Start Period"
//             className={this.props.classes.textField}
//             InputLabelProps={{ shrink: true }}
//             value={this.props.selectedTask.plannedStartPeriod}
//           />
//           <TextField
//             readOnly={true}
//             key={this.props.view.selectedTaskId + "_plannedEndPeriod"}
//             id="plannedEndPeriod"
//             label="End Period"
//             className={this.props.classes.textField}
//             InputLabelProps={{ shrink: true }}
//             value={this.props.selectedTask.plannedEndPeriod}
//           />
//           <TextField
//             readOnly={true}
//             key={this.props.view.selectedTaskId + "_forecastedEndPeriod"}
//             id="forecastedEndPeriod"
//             label="Forecasted End Period"
//             className={this.props.classes.textField}
//             InputLabelProps={{ shrink: true }}
//             value={this.props.selectedTask.forecastedEndPeriod}
//           />
//         </div>
//         <Typography color="textPrimary" variant="h6">
//           Distribution
//         </Typography>
//         <div>
//           <TaskDistributionPicker
//             task={this.props.selectedTask}
//             classes={this.props.classes}
//           />
//         </div>
//         <hr />
//         <div>
//           <ButtonGroup variant="text" color="primary">
//             <Button onClick={() => this.handleActual()}>Actuals</Button>
//             <Button onClick={() => this.handleAnalysis()}>Analysis</Button>
//             <Button onClick={() => this.handleBaseline()}>Baselines</Button>
//           </ButtonGroup>
//         </div>
//         <div width="600px">
//           <View
//             task={this.props.selectedTask}
//             view={this.state.selectedTab}
//             classes={this.props.classes}
//           />
//         </div>
//       </Paper>
//     );
//   }
// }

// TaskPanel.propTypes = {
//   classes: PropTypes.object.isRequired,
// };

export default observer(TaskPanel);
